import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  Users,
  Clock,
  Eye,
  ArrowLeft,
  AlertTriangle,
  CalendarDays,
  Timer,
} from "lucide-react";
import DatePicker from "react-datepicker";
import Header from "../../Header";
import LeftNavBar from "../../LeftNavBar";
import PageHeader from "../../PageHeader";
import { PageHeaderName } from "../../../StyledCss/styledComponents";
import { PrimaryButton } from "../../../StyledCss/styledbtn";
import LoadingView from "../../LoadingView";
import FailureView from "../../FailureView";
import { StatCard } from "../StatCard";

import { getEmployeesSummaryByProject } from "../../../api/services/hroneApi";
import { convertKeysToCamelCase } from "../../../util/convertCamelCase";
import { usePermissions } from "../../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../../util/featureCodes";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./index.css";
import "../index.css";
const apiStatusConstants = {
  initial: "INITIAL",
  success: "SUCCESS",
  failure: "FAILURE",
  inProgress: "IN_PROGRESS",
};

const EmployeesByProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = usePermissions();
  const canViewAll = hasPermission(FEATURE_CODES.WORKLOG_VIEW_ALL);
  const [apiStatus, setApiStatus] = useState(apiStatusConstants.initial);
  const [employeesData, setEmployeesData] = useState([]);
  const [totalEmployeeData, setTotalEmployeeData] = useState({});
  const [projectName, setProjectName] = useState("");

  const [startMonth, setStartMonth] = useState(new Date());
  const [endMonth, setEndMonth] = useState(new Date());
  const [isSorted, setIsSorted] = useState(false);
  const [originalEmployeesData, setOriginalEmployeesData] = useState([]);


  const statsData = useMemo(() => {
    return [
      {
        title: "Total Employees",
        value: totalEmployeeData.totalEmployees || 0,
        icon: Users,
        accentColor: "#2563EB",
      },
      {
        title: "Total Working Days",
        value: totalEmployeeData.totalWorkingDays || 0,
        icon: CalendarDays,
        accentColor: "#059669",
      },
      {
        title: "Total Billable Hours",
        value: totalEmployeeData.actualWorkingHours || 0,
        icon: Clock,
        accentColor: "#4F46E5",
      },
      {
        title: "Logged Billable Hours",
        value: totalEmployeeData.loggedHours || 0,
        icon: Timer,
        accentColor: "#DB2777",
      },
    ];
  }, [totalEmployeeData]);

  const handleCardClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleViewUser = useCallback(
    (data) => {
      navigate(`/worklogs_employee/${id}/${data.employeeId}`);
    },
    [navigate, id],
  );

  const handlePendingSort = useCallback(() => {
    if (isSorted) {
      setEmployeesData(originalEmployeesData);
      setIsSorted(false);
    } else {
      const sorted = [...originalEmployeesData].sort((a, b) => {
        if (a.lowPerformance === true && b.lowPerformance !== true) return -1;
        if (a.lowPerformance !== true && b.lowPerformance === true) return 1;
        return 0;
      });
      setEmployeesData(sorted);
      setIsSorted(true);
    }
  }, [isSorted, originalEmployeesData]);

  const fetchEmployeesSummary = useCallback(async () => {
    try {
      setApiStatus(apiStatusConstants.inProgress);
      const startDate = new Date(
        startMonth.getFullYear(),
        startMonth.getMonth(),
        1,
      );
      const endDate = new Date(
        endMonth.getFullYear(),
        endMonth.getMonth() + 1,
        0,
      );

      // Format to YYYY-MM-DD
      const formatToYMD = (date) =>
        date.getFullYear() +
        "-" +
        String(date.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(date.getDate()).padStart(2, "0");
      const formattedStartDate = formatToYMD(startDate);
      const formattedEndDate = formatToYMD(endDate);

      const response = await getEmployeesSummaryByProject(
        id,
        formattedStartDate,
        formattedEndDate,
      );

      if (response.ok) {
        const data = convertKeysToCamelCase(await response.json());
        setEmployeesData(data.employees);
        setOriginalEmployeesData(data.employees);
        setTotalEmployeeData(data.allEmployees || {});
        setProjectName(data.allEmployees?.projectName || "");
        setApiStatus(apiStatusConstants.success);
      } else {
        setApiStatus(apiStatusConstants.failure);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setApiStatus(apiStatusConstants.failure);
    }
  }, [startMonth, endMonth, id]);
  
  useEffect(() => {
    if (!canViewAll) {
      navigate("/worklogs", { replace: true });
      return;
    }
    fetchEmployeesSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMonth, endMonth, canViewAll]);

  const columnDefs = useMemo(
    () => [
      { headerName: "EMPLOYEE ID", field: "employeeId", width: 300, suppressMenu: true },
      { headerName: "EMPLOYEE NAME", field: "employeeName", width: 300, suppressMenu: true },
      { headerName: "STATUS", field: "status", width: 200, suppressMenu: true },
      {
        headerName: "TOTAL WORKED DAYS",
        field: "totalWorkingDays",
        width: 300,
        suppressMenu: true
      },
      { headerName: "TOTAL HOURS", field: "loggedHours", width: 300, suppressMenu: true },
      {
        headerName: "ACTIONS",
        width: 200,
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              height: "100%",
            }}
          >
            <div title="View" className="icon-wrapper">
              <Eye
                style={{
                  cursor: "pointer",
                  color: "#28a745",
                  fontSize: "20px",
                }}
                onClick={() => handleViewUser(params.data)}
              />
            </div>
            <div title={`Pending Logs: ${params.data.weeklyWorkedHours} hrs  / ${params.data.weeklyAssignedHours} hrs`} className="icon-wrapper">
              {params.data.lowPerformance === true && (
                <AlertTriangle style={{ color: "red", fontSize: "20px" }} />
              )}
            </div>
          </div>
        ),
        sortable: false,
        filter: false,
        floatingFilter: false,
      },
    ],
    [handleViewUser],
  );

  const defaultColDef = useMemo(
    () => ({
      cellStyle: { fontSize: "14px" },
      sortable: true,
      filter: true,
      floatingFilter: true,
    }),
    [],
  );

  const renderTable = useCallback(
    () => (
      <div
        className="ag-theme-alpine"
        style={{
          height: "78vh",
          fontSize: "14px",
          marginLeft: "20px",
          marginRight: "20px",
          marginTop: "20px",
          fontFamily: "Arial, Helvetica, sans-serif",
          backgroundColor: "#FFFFFF",
        }}
      >
        <AgGridReact
          columnDefs={columnDefs}
          rowData={employeesData}
          pagination={true}
          paginationPageSize={10}
          defaultColDef={defaultColDef}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          enableCellTextSelection={true}
        />
      </div>
    ),
    [columnDefs, employeesData, defaultColDef],
  );

  const renderData = () => {
    switch (apiStatus) {
      case apiStatusConstants.inProgress:
        return <LoadingView />;
      case apiStatusConstants.success:
        return renderTable();
      case apiStatusConstants.failure:
        return <FailureView onClickRetry={fetchEmployeesSummary} />;
      default:
        return null;
    }
  };

  return (
    <div>
      <Header />
      <div className="d-flex">
        <LeftNavBar />
        <div className="page-content-wrapper">
          <PageHeader>
            <PageHeaderName
            >
              {" "}
              <ArrowLeft size={20} 
              onClick={handleCardClick}
              style={{ cursor: "pointer" }} /> {projectName} - Worklogs
            </PageHeaderName>
          </PageHeader>

          <div className="row g-3 mx-0">
            {statsData.map((item, index) => (
              <div
                key={index}
                className="col-6 col-md-3 col-lg-3 col-xl-2 d-flex"
              >
                <StatCard {...item} />
              </div>
            ))}
          </div>

          <div className="d-flex justify-content-between">
            <h4 className="fw-semibold m-3">Employees Overview</h4>
            <div className="d-flex justify-content-center align-items-center gap-4 ">
              <PrimaryButton
                style={{ width: "130px" }}
                onClick={handlePendingSort}
              >
                {isSorted ? " All Employees" : "Pending Logs"}
              </PrimaryButton>
              <div className="d-flex flex-row flex-wrap justify-content-end align-items-center mb-2">
                <div className="month-picker d-flex flex-column m-1">
                  <label className="mb-1 ">Start Month</label>
                  <DatePicker
                    className="date-input-label"
                    selected={startMonth}
                    onChange={(date) => {
                      if (date) {
                        setStartMonth(date);
                        if (date > endMonth) {
                            setEndMonth(date);
                          }
                      } else {
                        setStartMonth(new Date());
                      }
                    }}
                    showMonthYearPicker
                    dateFormat="MMMM yyyy"
                  />
                </div>
                <div className="month-picker d-flex flex-column m-1">
                  <label className="mb-1 ">End Month</label>
                  <DatePicker
                    className="date-input-label"
                    selected={endMonth}
                    onChange={(date) => {
                      if (date) {
                        setEndMonth(date);
                      } else {
                        setEndMonth(new Date());
                      }
                    }}
                    showMonthYearPicker
                    dateFormat="MMMM yyyy"
                    minDate={startMonth}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="table-container">{renderData()}</div>
        </div>
      </div>
    </div>
  );
};

export default EmployeesByProject;
